<template>
  <ts-loading-banner :loading="waiting">
    <div class="ts-space-y-4">
      <div class="row">
        <div class="col-4 ts-space-y-1">
          <label class="required">{{ $t('allAboutMyDay.class') }}</label>
          <a-select
            style="width: 100%"
            v-model.number="model.class_id"
            :options="classlists"
            :disabled="showOnly || isUpdate"
          ></a-select>
          <div class="validate" v-if="errors.has('model.class_id')">
            {{ errors.first('model.class_id') }}
          </div>
        </div>

        <div class="col-4 ts-space-y-1">
          <label class="required">{{ $t('allAboutMyDay.student') }}</label>
          <a-select
            style="width: 100%"
            v-model="model.student_id"
            :options="studentlists"
            :show-search="true"
            :maxTagCount="2"
            :loading="fetching_student"
            :disabled="showOnly || isUpdate"
            :filter-option="
              (input, option) =>
                option.componentOptions.children[0].text
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
            "
            @change="checkData()"
          ></a-select>
          <div class="validate" v-if="errors.has('student_id')">
            {{ errors.first('student_id') }}
          </div>
        </div>
        <div class="col-4 ts-space-y-1">
          <label class="required">{{ $t('allAboutMyDay.date') }}</label>
          <a-date-picker
            v-model="model.activity_date"
            style="width: 100%"
            placeholder="DD-MM-YYYY"
            valueFormat="DD-MM-YYYY"
            format="DD-MM-YYYY"
            @change="onChangeActivityDate"
            :disabled="showOnly"
          />
          <div class="invalid-feedback" v-if="errors.has('activity_date')">
            {{ errors.first('activity_date') }}
          </div>
        </div>
      </div>
      <div class="ts-space-y-1">
        <div class="row">
          <label
            class="ts-whitespace-no-wrap col-form-label col-4 ts-align-top ts-pt-0"
            >{{ $t('allAboutMyDay.specialActivitiesToday') }}
          </label>
          <div class="col-8">
            <textarea
              v-model="model.special_activity"
              :disabled="showOnly || isStaff"
              rows="2"
              class="form-control"
            ></textarea>
          </div>
        </div>

        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-3"
            >{{ $t('allAboutMyDay.myTemperatureWas') }}
          </label>
          <div class="col-9">
            <input
              :disabled="showOnly || isTeacher"
              v-model="model.my_temperature_was"
              type="text"
              class="form-input"
              :class="{ 'is-invalid': errors.has('special_activity') }"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <div class="row">
              <label class="ts-whitespace-no-wrap col-form-label col-3"
                >{{ $t('allAboutMyDay.InappedFrom') }}
              </label>
              <div class="col-4">
                <a-time-picker
                  class="ts-w-full"
                  v-model="model.napped_from"
                  value-format="HH:mm"
                  format="HH:mm"
                  :bordered="false"
                  allowClear
                  :disabled="showOnly || isTeacher"
                />
              </div>
              <label class="col-form-label">to</label>
              <div class="col-4">
                <a-time-picker
                  class="ts-w-full"
                  v-model="model.napped_to"
                  value-format="HH:mm"
                  format="HH:mm"
                  :bordered="false"
                  allowClear
                  :disabled="showOnly || isTeacher"
                />
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="row">
              <label class="ts-whitespace-no-wrap col-form-label col-4"
                >{{ $t('allAboutMyDay.myMoodTodayWas') }}
              </label>
              <div class="col-8">
                <input
                  :disabled="showOnly || isTeacher"
                  v-model="model.my_mood_today_was"
                  type="text"
                  class="form-input"
                  :class="{ 'is-invalid': errors.has('special_activity') }"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-3"
            >{{ $t('allAboutMyDay.forMorningSnackIHad') }}
          </label>
          <div class="col-9">
            <input
              :disabled="showOnly || isTeacher"
              v-model="model.for_morning_snack_i_had"
              type="text"
              class="form-input"
              :class="{ 'is-invalid': errors.has('special_activity') }"
            />
          </div>
        </div>

        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-3"
            >{{ $t('allAboutMyDay.forLunchIHad') }}
          </label>
          <div class="col-9">
            <input
              :disabled="showOnly || isTeacher"
              v-model="model.for_lunch_i_had"
              type="text"
              class="form-input"
              :class="{ 'is-invalid': errors.has('special_activity') }"
            />
          </div>
        </div>

        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-3"
            >{{ $t('allAboutMyDay.forAfternoonSnackIHad') }}
          </label>
          <div class="col-9">
            <input
              :disabled="showOnly || isTeacher"
              v-model="model.for_afternoon_snack_i_had"
              type="text"
              class="form-input"
              :class="{ 'is-invalid': errors.has('special_activity') }"
            />
          </div>
        </div>

        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-3"
            >{{ $t('allAboutMyDay.todayIDrankWater') }}
          </label>

          <div class="col-9">
            <div class="row">
              <div class="col-3 col-form-label">
                <a-checkbox
                  v-model="model.drank_1bottle_water"
                  @change="onDrank1bottleWater()"
                  :disabled="showOnly || isTeacher"
                  >1 bottle</a-checkbox
                >
              </div>
              <div class="col-3 col-form-label">
                <a-checkbox
                  v-model="model.drank_2bottle_water"
                  @change="onDrank2bottleWater()"
                  :disabled="showOnly || isTeacher"
                  >2 bottles</a-checkbox
                >
              </div>
              <div class="col-3 col-form-label">
                <a-checkbox
                  v-model="model.drank_3bottle_water"
                  @change="onDrank3bottleWater()"
                  :disabled="showOnly || isTeacher"
                  >3 bottles</a-checkbox
                >
              </div>
              <div class="col-3 col-form-label">
                <a-checkbox
                  v-model="model.drank_4bottle_water"
                  @change="onDrank4bottleWater()"
                  :disabled="showOnly || isTeacher"
                  >4 bottles</a-checkbox
                >
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-3">{{
            $t('allAboutMyDay.todayIDrankMilk')
          }}</label>
          <div class="col-9">
            <div class="row">
              <div class="col-3 col-form-label">
                <a-checkbox
                  v-model="model.drank_1bottle_milk"
                  @change="onDrank1bottleMilk()"
                  :disabled="showOnly || isTeacher"
                  >1 bottle</a-checkbox
                >
              </div>
              <div class="col-3 col-form-label">
                <a-checkbox
                  v-model="model.drank_2bottle_milk"
                  @change="onDrank2bottleMilk()"
                  :disabled="showOnly || isTeacher"
                  >2 bottles</a-checkbox
                >
              </div>
              <div class="col-3 col-form-label">
                <a-checkbox
                  v-model="model.drank_3bottle_milk"
                  @change="onDrank3bottleMilk()"
                  :disabled="showOnly || isTeacher"
                  >3 bottles</a-checkbox
                >
              </div>
              <div class="col-3 col-form-label">
                <a-checkbox
                  v-model="model.drank_4bottle_milk"
                  @change="onDrank4bottleMilk()"
                  :disabled="showOnly || isTeacher"
                  >4 bottles</a-checkbox
                >
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <label class="ts-whitespace-no-wrap col-form-label col-3"
            >{{ $t('allAboutMyDay.IHadMyDiaperChangedAt') }}
          </label>
          <div class="col-3">
            <a-time-picker
              class="ts-w-full"
              v-model="model.diaper_changed_at"
              value-format="HH:mm"
              format="HH:mm"
              :bordered="false"
              allowClear
              :disabled="showOnly || isTeacher"
            />
          </div>
          <label class="col-form-label">and</label>
          <div class="col-3">
            <a-time-picker
              class="ts-w-full"
              v-model="model.diaper_changed_and"
              value-format="HH:mm"
              format="HH:mm"
              :bordered="false"
              allowClear
              :disabled="showOnly || isTeacher"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="row">
              <label class="ts-whitespace-no-wrap col-form-label col-12">{{
                $t('allAboutMyDay.PleaseBringFollowingSuppliesForMe')
              }}</label>
            </div>
            <div class="row">
              <div class="col-12">
                <textarea
                  v-model="model.bring_following_supplies"
                  :disabled="showOnly || isTeacher"
                  rows="4"
                  class="form-control"
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="row">
              <label class="ts-whitespace-no-wrap col-form-label col-12">{{
                $t('allAboutMyDay.messageFromTeacher')
              }}</label>
            </div>
            <div class="row">
              <div class="col-12">
                <textarea
                  v-model="model.message_from_teacher"
                  :disabled="showOnly || isStaff"
                  rows="4"
                  class="form-control"
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="row">
              <label class="ts-whitespace-no-wrap col-form-label col-12">{{
                $t('allAboutMyDay.messageFromParents')
              }}</label>
            </div>
            <div class="row">
              <div class="col-12">
                <textarea
                  disabled
                  v-model="model.message_from_parent"
                  rows="4"
                  class="form-control"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row ts-mt-2" v-if="!showOnly">
        <div class="col-md-12 ts-flex ts-justify-end ts-space-x-3">
          <ts-button class="mr-2" @click.prevent="() => $emit('cancel')">
            {{ $t('cancel') }}
          </ts-button>
          <ts-button
            v-if="!isUpdate"
            color="primary"
            outline
            :waiting="waiting_new"
            :disabled="waiting_new || waiting"
            @click.prevent="onSaveAddNew"
          >
            {{ $t('save_add_new') }}</ts-button
          >
          <ts-button v-if="!isUpdate" color="primary" @click.prevent="onSave">{{
            $t('save')
          }}</ts-button>
          <ts-button
            v-if="isUpdate"
            color="primary"
            @click.prevent="onUpdate"
            >{{ $t('update') }}</ts-button
          >
        </div>
      </div>
    </div>
  </ts-loading-banner>
</template>

<script>
import { isEmpty, debounce } from 'lodash'
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'

export default {
  name: 'allAboutMyDayForm',
  props: ['showOnlyData'],
  components: {},
  data () {
    return {
      objectContain: false,
      waiting: false,
      waiting_new: false,
      showOnly: false,
      fetching_student: false,
      errors: new Errors(),
      classlists: [],
      studentlists: [],
      model: {
        id: null,
        student_id: null,
        class_id: null,
        activity_date: moment().format('DD-MM-YYYY'),
        my_temperature_was: null,
        special_activity: null,
        napped_from: null,
        napped_to: null,
        my_mood_today_was: null,
        for_morning_snack_i_had: null,
        for_lunch_i_had: null,
        for_afternoon_snack_i_had: null,
        drank_1bottle_water: false,
        drank_2bottle_water: false,
        drank_3bottle_water: false,
        drank_4bottle_water: false,
        drank_1bottle_milk: false,
        drank_2bottle_milk: false,
        drank_3bottle_milk: false,
        drank_4bottle_milk: false,
        diaper_changed_at: null,
        diaper_changed_and: null,
        bring_following_supplies: null,
        message_from_teacher: null,
        message_from_parent: null
      }
    }
  },
  async created () {
    this.showOnly = this.showOnlyData
    await this.setEditData()
    await this.fetchClass()
  },
  computed: {
    ...mapState('allAboutMyDay', ['edit_data']),
    isUpdate () {
      return !isEmpty(this.edit_data)
    },
    isStaff () {
      if (this.AllowAllAboutMyDay) {
        return false
      } else {
        return this.$store.getters.isStaffLogin
      }
    },
    isTeacher () {
      if (this.AllowAllAboutMyDay) {
        return false
      } else {
        return this.$store.getters.isTeacherLogin
      }
    },
    AllowAllAboutMyDay () {
      return this.$store.state.authUser.is_allow_full_all_about_my_day
        ? this.$store.state.authUser.is_allow_full_all_about_my_day
        : false
    }
  },
  methods: {
    ...mapActions('allAboutMyDay', ['getClass', 'getStudent', 'getFindData']),

    onDrank1bottleMilk () {
      this.model.drank_2bottle_milk = false
      this.model.drank_3bottle_milk = false
      this.model.drank_4bottle_milk = false
    },
    onDrank2bottleMilk () {
      this.model.drank_1bottle_milk = false
      this.model.drank_3bottle_milk = false
      this.model.drank_4bottle_milk = false
    },
    onDrank3bottleMilk () {
      this.model.drank_1bottle_milk = false
      this.model.drank_2bottle_milk = false
      this.model.drank_4bottle_milk = false
    },
    onDrank4bottleMilk () {
      this.model.drank_1bottle_milk = false
      this.model.drank_2bottle_milk = false
      this.model.drank_3bottle_milk = false
    },
    onDrank1bottleWater () {
      this.model.drank_2bottle_water = false
      this.model.drank_3bottle_water = false
      this.model.drank_4bottle_water = false
    },
    onDrank2bottleWater () {
      this.model.drank_1bottle_water = false
      this.model.drank_3bottle_water = false
      this.model.drank_4bottle_water = false
    },
    onDrank3bottleWater () {
      this.model.drank_1bottle_water = false
      this.model.drank_2bottle_water = false
      this.model.drank_4bottle_water = false
    },
    onDrank4bottleWater () {
      this.model.drank_1bottle_water = false
      this.model.drank_2bottle_water = false
      this.model.drank_3bottle_water = false
    },
    fetchClass (params) {
      this.waiting = true
      this.getClass(params)
        .then(response => {
          this.classlists = response.data.map(el => ({
            value: el.class_id,
            label: el.class_name
          }))
        })
        .finally(() => {
          this.waiting = false
        })
    },
    fetchStudentList () {
      this.waiting = true
      this.getStudent({ class_id: this.model.class_id })
        .then(response => {
          this.studentlists = response.data.map(el => ({
            value: el.student_id,
            label: el.card_number + '|' + el.full_name_en + '|' + el.gender
          }))
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onChangeActivityDate (dateString) {
      this.model.activity_date = dateString
      this.checkData()
    },
    checkData () {
      this.waiting = true
      this.getFindData({
        class_id: this.model.class_id,
        student_id: this.model.student_id,
        activity_date: this.model.activity_date
      })
        .then(async response => {
          if (response.data.length > 0) {
            await this.setFindData(response.data[0])
          } else {
            this.clearFindData()
          }
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onSave () {
      this.errors = new Errors()
      this.waiting = true
      this.$store
        .dispatch('allAboutMyDay/store', Object.assign({}, this.model))
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onSaveAddNew () {
      this.errors = new Errors()
      this.waiting = true
      this.$store
        .dispatch('allAboutMyDay/store', Object.assign({}, this.model))
        .then(response => {
          this.$toasted.success(response.message)
          this.clearEdit()
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onUpdate () {
      this.waiting = true
      this.$store
        .dispatch('allAboutMyDay/update', this.model)
        .then(response => {
          this.$toasted.success(response.message)
          this.$emit('fetchData')
          this.$emit('cancel')
        })
        .catch(error => {
          this.errors = new Errors(error.errors)
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    setEditData () {
      if (!isEmpty(this.edit_data)) {
        this.model.id = this.edit_data.id
        this.model.student_id = this.edit_data.student_id
        this.model.class_id = this.edit_data.class_id
        this.model.activity_date = this.edit_data.activity_date
        this.model.my_temperature_was = this.edit_data.my_temperature_was
        this.model.special_activity = this.edit_data.special_activity
        this.model.morning_snack_fruit = this.edit_data.morning_snack_fruit
        this.model.napped_from = this.edit_data.napped_from
        this.model.napped_to = this.edit_data.napped_to
        this.model.my_mood_today_was = this.edit_data.my_mood_today_was
        this.model.for_morning_snack_i_had =
          this.edit_data.for_morning_snack_i_had
        this.model.for_lunch_i_had = this.edit_data.for_lunch_i_had
        this.model.for_afternoon_snack_i_had =
          this.edit_data.for_afternoon_snack_i_had
        this.model.drank_1bottle_milk = this.edit_data.drank_1bottle_milk
        this.model.drank_2bottle_milk = this.edit_data.drank_2bottle_milk
        this.model.drank_3bottle_milk = this.edit_data.drank_3bottle_milk
        this.model.drank_4bottle_milk = this.edit_data.drank_4bottle_milk
        this.model.drank_1bottle_water = this.edit_data.drank_1bottle_water
        this.model.drank_2bottle_water = this.edit_data.drank_2bottle_water
        this.model.drank_3bottle_water = this.edit_data.drank_3bottle_water
        this.model.drank_4bottle_water = this.edit_data.drank_4bottle_water
        this.model.diaper_changed_at = this.edit_data.diaper_changed_at
        this.model.diaper_changed_and = this.edit_data.diaper_changed_and
        this.model.bring_following_supplies =
          this.edit_data.bring_following_supplies
        this.model.message_from_teacher = this.edit_data.message_from_teacher
        this.model.message_from_parent = this.edit_data.message_from_parent
      }
    },
    setFindData (data) {
      if (!isEmpty(data)) {
        this.model.id = data.id
        this.model.student_id = data.student_id
        this.model.class_id = data.class_id
        this.model.activity_date = data.activity_date
        this.model.my_temperature_was = data.my_temperature_was
        this.model.special_activity = data.special_activity
        this.model.morning_snack_fruit = data.morning_snack_fruit
        this.model.napped_from = data.napped_from
        this.model.napped_to = data.napped_to
        this.model.my_mood_today_was = data.my_mood_today_was
        this.model.for_morning_snack_i_had = data.for_morning_snack_i_had
        this.model.for_lunch_i_had = data.for_lunch_i_had
        this.model.for_afternoon_snack_i_had = data.for_afternoon_snack_i_had
        this.model.drank_1bottle_milk = data.drank_1bottle_milk
        this.model.drank_2bottle_milk = data.drank_2bottle_milk
        this.model.drank_3bottle_milk = data.drank_3bottle_milk
        this.model.drank_4bottle_milk = data.drank_4bottle_milk
        this.model.drank_1bottle_water = data.drank_1bottle_water
        this.model.drank_2bottle_water = data.drank_2bottle_water
        this.model.drank_3bottle_water = data.drank_3bottle_water
        this.model.drank_4bottle_water = data.drank_4bottle_water
        this.model.diaper_changed_at = data.diaper_changed_at
        this.model.diaper_changed_and = data.diaper_changed_and
        this.model.bring_following_supplies = data.bring_following_supplies
        this.model.message_from_teacher = data.message_from_teacher
        this.model.message_from_parent = data.message_from_parent
      }
    },
    clearEdit () {
      this.model.id = null
      this.model.student_id = null
      this.model.class_id = null
      this.model.activity_date = moment().format('DD-MM-YYYY')
      this.model.my_temperature_was = null
      this.model.special_activity = null
      this.model.morning_snack_fruit = false
      this.model.napped_from = null
      this.model.napped_to = null
      this.model.my_mood_today_was = null
      this.model.for_morning_snack_i_had = null
      this.model.for_lunch_i_had = null
      this.model.for_afternoon_snack_i_had = null
      this.model.drank_1bottle_milk = false
      this.model.drank_2bottle_milk = false
      this.model.drank_3bottle_milk = false
      this.model.drank_4bottle_milk = false
      this.model.drank_1bottle_water = false
      this.model.drank_2bottle_water = false
      this.model.drank_3bottle_water = false
      this.model.drank_4bottle_water = false
      this.model.diaper_changed_at = null
      this.model.diaper_changed_and = null
      this.model.bring_following_supplies = null
      this.model.message_from_teacher = null
      this.model.message_from_parent = null
      this.showOnly = false
    },
    clearFindData () {
      this.model.id = null
      this.model.my_temperature_was = null
      this.model.special_activity = null
      this.model.morning_snack_fruit = false
      this.model.napped_from = null
      this.model.napped_to = null
      this.model.my_mood_today_was = null
      this.model.for_morning_snack_i_had = null
      this.model.for_lunch_i_had = null
      this.model.for_afternoon_snack_i_had = null
      this.model.drank_1bottle_milk = false
      this.model.drank_2bottle_milk = false
      this.model.drank_3bottle_milk = false
      this.model.drank_4bottle_milk = false
      this.model.drank_1bottle_water = false
      this.model.drank_2bottle_water = false
      this.model.drank_3bottle_water = false
      this.model.drank_4bottle_water = false
      this.model.diaper_changed_at = null
      this.model.diaper_changed_and = null
      this.model.bring_following_supplies = null
      this.model.message_from_teacher = null
      this.model.message_from_parent = null
      this.showOnly = false
    }
  },
  watch: {
    'model.class_id': debounce(function () {
      this.fetchStudentList()
    }, 500)
  }
}
</script>

<style lang="scss" scoped>
.form-input {
  line-height: 1.42857143;
  padding: 6px 12px;
  width: 100%;
  border-bottom: dotted 1px;
}
.form-input:focus {
  outline: none;
}
</style>
